/**
 * fleet-composition-tooltip.ts — v4M6.6 in-flight fleet hover panel.
 *
 * DOM tooltip companion to `jp-tooltip.ts`. Activates when the input
 * handler sets `ui.hoveredInFlightShipId` (set on mousemove hit-test
 * against in-flight ship dots in galaxy view; see `src/ui/input.ts`
 * `hitTestInFlightShips`). Reads the matching ship's FoW classification
 * via `classifyShipForViewer` (v4M6.3b) and surfaces:
 *
 *  - **Own / teammate fleets** — full chassis breakdown via
 *    `aggregateInFlightFleet` (v4M6.3b). "Heading to <System> · 3×
 *    Scout, 1× Cruiser · ETA 4.2s".
 *  - **Visible enemy fleets** — degraded readout per `MAP.md §4.3.2`
 *    intel asymmetry: "Enemy fleet · 4 ships en route". No chassis
 *    breakdown, no ETA, no destination name.
 *
 * Hidden ships never reach the hover hit-test (`classifyShipForViewer`
 * filters them out at the input-handler gate), so the tooltip never
 * reveals them here either.
 *
 * Pure reader; never mutates state. Mirrors the v4M5.3b3e jp-tooltip
 * mount pattern (fixed DOM panel, anchored at `ui.hoveredGalaxyAnchor`,
 * positioned a few px below + right of the cursor so the tooltip
 * doesn't sit under the cursor itself).
 */

import {
  aggregateInFlightFleet,
  classifyShipForViewer,
  formatEta,
  type FleetGroup,
} from '../render/galaxy-paths';
import type { GameState } from '../state/types/index';
import type { UiState } from './state';

export interface FleetCompositionTooltipHandle {
  update(state: GameState, ui: UiState): void;
  destroy(): void;
}

/**
 * Format chassis counts as "3× Scout, 1× Cruiser". Iterates in chassis
 * ascending order (Map insertion order isn't reliable across the
 * aggregator's iteration, so we sort explicitly for deterministic output).
 *
 * Exported for unit testing — the DOM widget calls this internally.
 */
export function formatChassisLine(chassisCounts: Map<string, number>): string {
  const entries = [...chassisCounts.entries()].sort((a, b) =>
    a[0].localeCompare(b[0]),
  );
  return entries.map(([chassis, count]) => `${count}× ${chassis}`).join(', ');
}

export function mountFleetCompositionTooltip(
  parent: HTMLElement,
): FleetCompositionTooltipHandle {
  const root = document.createElement('div');
  root.id = 'fleet-composition-tooltip';
  Object.assign(root.style, {
    position: 'fixed',
    left: '0px',
    top: '0px',
    padding: '6px 8px',
    background: 'rgba(10, 14, 24, 0.92)',
    border: '1px solid rgba(120, 160, 230, 0.4)',
    borderRadius: '3px',
    color: '#d8e4ff',
    font: '11px / 1.35 monospace',
    pointerEvents: 'none',
    zIndex: '20',
    display: 'none',
    whiteSpace: 'nowrap',
  } as Partial<CSSStyleDeclaration>);
  parent.appendChild(root);

  return {
    update(state: GameState, ui: UiState): void {
      const hoveredId = ui.hoveredInFlightShipId;
      const anchor = ui.hoveredGalaxyAnchor;
      if (
        hoveredId === null ||
        !anchor ||
        ui.viewMode !== 'galaxy'
      ) {
        root.style.display = 'none';
        return;
      }
      const ship = state.ships.get(hoveredId);
      if (!ship) {
        root.style.display = 'none';
        return;
      }
      const fow = classifyShipForViewer(state, ui, ship);
      if (fow === 'hidden') {
        root.style.display = 'none';
        return;
      }

      // Look up the ship's group via the v4M6.3b aggregator. Charge-up
      // ships are also surfaced — they have a `pendingJumpSystemId`
      // destination — but the hover hit-test only fires on mid-jump
      // ships (the input handler uses `inFlightShipScreenPos`, which
      // returns null during charge-up). So in practice every tooltip
      // here is for a mid-jump fleet; the aggregator key matches.
      const groups = aggregateInFlightFleet(state);
      const key = `${ship.ownerEmpireId}:${ship.jumpOriginSystemId}:${ship.jumpTargetSystemId}`;
      const group: FleetGroup | undefined = groups.get(key);

      if (fow === 'enemy-visible') {
        // Degraded readout — count only, no chassis, no destination.
        const count = group?.count ?? 1;
        root.textContent = `Enemy fleet · ${count} ship${count === 1 ? '' : 's'} en route`;
      } else {
        // Own / teammate — full chassis breakdown + destination + ETA.
        const dest =
          ship.jumpTargetSystemId !== null
            ? state.systems.get(ship.jumpTargetSystemId)
            : null;
        const destName = dest?.name ?? '?';
        const chassisLine = group
          ? formatChassisLine(group.chassisCounts)
          : `1× ${ship.chassis}`;
        const eta = formatEta(ship.jumpTicksRemaining);
        root.textContent = `Heading to ${destName} · ${chassisLine} · ${eta}`;
      }

      // Position the tooltip a few px below + right of the cursor so
      // it doesn't sit under the cursor itself.
      root.style.left = `${Math.round(anchor.x + 14)}px`;
      root.style.top = `${Math.round(anchor.y + 14)}px`;
      root.style.display = 'block';
    },
    destroy(): void {
      root.remove();
    },
  };
}
